import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setCustomerReference2Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference2ErrorHandler";
import setcustomerReference2 from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference2Handler";

import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState = getCurrentCartState(state).value;

    return {
        cartState,
        currentBillTo: getBillToState(state, cartState?.billToId).value,
        customerReference2Error: state.KGPState.CheckoutReview.customerReference2Error,
        customerReference2: state.KGPState.CheckoutReview.customerReference2,
        isAwaitingApproval: cartState?.isAwaitingApproval,
    };
};

const mapDispatchToProps = {
    setcustomerReference2,
    setCustomerReference2Error,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    customerReferenceGrid?: GridItemProps;
    customerReferenceChildGrid?: GridItemProps;
    customerReferenceSelect?: SelectPresentationProps;
    customerOrderReferenceLabelStyle?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    customerReferenceGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 3px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerReferenceChildGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 20px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerOrderReferenceLabelStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: 600;
        `,
    },
};

export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CustomerReference2Field: FC<Props> = ({
    setcustomerReference2,
    currentBillTo,
    customerReference2,
    customerReference2Error,
    setCustomerReference2Error,
    isAwaitingApproval,
    cartState,
}) => {
    const [properties, setProperties] = useState(currentBillTo?.properties);
    const [customerRefChild, setCustomerRefChild] = useState(customerReference2 || "");
    const [customerReference2Val, setCustomerReference2Val] = useState(customerReference2 || "");
    const [customerRefError, setCustomerRefError] = useState(
        properties?.customerOrderReferenceRequired2?.toLowerCase() === "true" ? customerReference2Error : "",
    );

    useEffect(() => {
        properties?.customerOrderReferenceRequired2?.toLowerCase() === "true" &&
            setCustomerRefError(customerReference2Error);
    }, [customerReference2Error]);

    useEffect(() => {
        setProperties(currentBillTo?.properties);
    }, [currentBillTo?.properties]);

    useEffect(() => {
        setcustomerReference2({ customerReference2: customerReference2Val || "" });
    }, [customerReference2Val]);

    useEffect(() => {
        setcustomerReference2({ customerReference2: customerRefChild || "" });
    }, [customerRefChild]);

    useEffect(() => {
        if (
            cartState?.properties &&
            cartState.properties?.customerOrderRefSelectedValue2 &&
            cartState.properties?.customerOrderRefSelectedValue2.split("|").length > 1
        ) {
            setCustomerReference2Val(cartState.properties.customerOrderRefSelectedValue2.split("|")[1]);
            setCustomerRefChild(cartState.properties.customerOrderRefSelectedValue2.split("|")[1]);
        }
    }, [cartState?.properties]);

    const customerReferenceDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel2 ?? "")} is required`
                : "";

        setCustomerRefChild(e.target.value);
        setcustomerReference2({ customerReference2: e.target.value });
        if (properties?.customerOrderReferenceRequired2?.toLowerCase() === "true") {
            setCustomerReference2Error({ customerReference2Error: error });
        }
    };

    const customerReferenceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel2 ?? "")} is required`
                : "";

        setCustomerReference2Val(e.target.value);
        setcustomerReference2({ customerReference2: e.target.value });
        if (properties?.customerOrderReferenceRequired2?.toLowerCase() === "true") {
            setCustomerReference2Error({ customerReference2Error: error });
        }
    };

    if (!properties?.customerOrderReferenceType2 || properties?.customerOrderReferenceAttribute2 === "None") {
        return null;
    }

    return (
        <GridContainer>
            <GridItem {...styles.customerReferenceChildGrid}>
                {properties?.customerOrderReferenceType2 === "Dropdown" ? (
                    <Select
                        {...styles.customerReferenceSelect}
                        label={properties.customerOrderReferenceLabel2}
                        value={customerRefChild}
                        onChange={customerReferenceDropDownChange}
                        ref={null}
                        required={properties?.customerOrderReferenceRequired2?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired2?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    >
                        <option value="">{translate("Select option")}</option>
                        {properties?.listReferencesValues2.split("|").map(method => (
                            <option key={method} value={method}>
                                {method}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        label={properties?.customerOrderReferenceLabel2}
                        value={customerReference2Val}
                        onChange={customerReferenceInputChange}
                        required={properties?.customerOrderReferenceRequired2?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired2?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReference2Field),
    definition: {
        displayName: "Customer Reference 2 Field",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
